import { Button } from '@ads/components-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeletePerson } from '@/features/student-management/hooks/useDeletePerson';
import { usePersonFilters, usePersonList } from '@/features/student-management/hooks/usePersonList';
import { useTogglePersonStatus } from '@/features/student-management/hooks/useTogglePersonStatus';
import { PersonFilterBar } from '@/features/student-management/ui/molecules/PersonFilterBar';
import { DeletePersonModal } from '@/features/student-management/ui/organisms/DeletePersonModal';
import { PersonTable } from '@/features/student-management/ui/organisms/PersonTable';
import { PersonListTemplate } from '@/features/student-management/ui/templates/PersonListTemplate';

export const Route = createFileRoute('/_authenticated/persons/')({
  component: PersonListPage,
});

interface ConfirmingPerson {
  id: number;
  name: string;
}

function PersonListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('personManagement');

  const [confirmingPerson, setConfirmingPerson] = useState<ConfirmingPerson | null>(null);

  const { data, isLoading, totalCount, currentPage, totalPages } = usePersonList();
  const { updateFilters } = usePersonFilters();
  const deletePerson = useDeletePerson();
  const { activate, deactivate } = useTogglePersonStatus();

  function handleDeleteRequest(id: number) {
    const person = data.find(p => p.id === id);
    const name = person ? `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() : String(id);
    setConfirmingPerson({ id, name });
  }

  function handleDeleteConfirm() {
    if (!confirmingPerson) return;
    deletePerson.mutate(
      { id: confirmingPerson.id },
      { onSettled: () => setConfirmingPerson(null) },
    );
  }

  return (
    <>
      <PersonListTemplate
        headerActions={
          <Button
            hierarchy="primary"
            size="lg"
            onClick={() => void navigate({ to: '/persons/create' })}
          >
            {t('personList.createButton')}
          </Button>
        }
        filterPanel={<PersonFilterBar />}
        table={
          <PersonTable
            data={data}
            isLoading={isLoading}
            totalCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={page => updateFilters({ page })}
            onEdit={id => void navigate({ to: '/persons/$id', params: { id: String(id) } })}
            onPayments={id =>
              void navigate({
                to: '/persons/$id/payments',
                params: { id: String(id) },
              })
            }
            onEnrollments={_id =>
              void navigate({
                to: '/enrollments',
              })
            }
            onAttachments={id =>
              void navigate({
                to: '/persons/$id',
                params: { id: String(id) },
                search: { tab: 'documents' },
              })
            }
            onResume={id =>
              void navigate({
                to: '/persons/$id/resume',
                params: { id: String(id) },
              })
            }
            onActivate={id => activate.mutate({ id })}
            onDeactivate={id => deactivate.mutate({ id })}
            onDelete={handleDeleteRequest}
          />
        }
      />

      <DeletePersonModal
        open={confirmingPerson !== null}
        personName={confirmingPerson?.name ?? null}
        isLoading={deletePerson.isPending}
        onClose={() => setConfirmingPerson(null)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
